import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Archive, CalendarClock, CircleCheck, Clock,
  Eye, History, ImagePlus, Loader2, Palette, Pencil, Play, Sparkles, X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";

const INPUT = "h-10 w-full rounded-lg border border-white/10 bg-black/40 px-3 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";
const LABEL = "mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500";

const STATUS_STYLE = {
  draft: "border-white/15 bg-white/5 text-neutral-300",
  published: "border-sky-500/30 bg-sky-500/10 text-sky-400",
  scheduled: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  paused: "border-neutral-500/30 bg-neutral-500/10 text-neutral-400",
  expired: "border-neutral-600/30 bg-neutral-600/10 text-neutral-500",
  archived: "border-red-900/40 bg-red-950/30 text-red-400/70",
};

function fmtDT(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true }); }
  catch { return v; }
}

function ColorField({ label, value, onChange, testid }) {
  const isHex = /^#[0-9a-fA-F]{6}$/.test(value || "");
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <div className="flex items-center gap-2">
        <span className="h-9 w-9 shrink-0 rounded-lg border border-white/15" style={{ background: value || "transparent" }} />
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className={`${INPUT} font-mono text-xs`} placeholder="#FFD98A" />
        {isHex && (
          <input type="color" value={value} onChange={(e) => onChange(e.target.value)}
            className="h-9 w-9 shrink-0 cursor-pointer rounded-lg border border-white/15 bg-transparent" aria-label={`${label} picker`} />
        )}
      </div>
    </div>
  );
}

function AssetField({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd);
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(apiError(e, "Upload failed")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        data-testid={`${testid}-file`} onChange={(e) => upload(e.target.files?.[0])} />
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-12 w-12 rounded-lg border border-white/10 bg-white/5 object-contain" />
        ) : (
          <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-white/15 text-neutral-600">
            <ImagePlus className="h-4 w-4" />
          </span>
        )}
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`${testid}-upload`}
          className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
          {value ? "Replace" : "Upload"}
        </button>
        {value && (
          <button onClick={() => onChange("")} data-testid={`${testid}-remove`}
            className="rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-neutral-400 hover:border-red-500/50 hover:text-red-400">
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminThemes() {
  const [data, setData] = useState(null);
  const [editor, setEditor] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: list }, { data: active }] = await Promise.all([
        api.get("/admin/themes"), api.get("/admin/themes/active"),
      ]);
      setData({ ...list, live: active });
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally { setBusy(false); }
  };

  if (!data) {
    return <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-amber-500" /></div>;
  }

  const live = data.live;
  const themes = data.themes.filter((t) => t.status !== "archived");
  const archivedThemes = data.themes.filter((t) => t.status === "archived");

  return (
    <div data-testid="admin-themes">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-white">Theme Management</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Festival themes. PAQVERO Default is always the fallback look.
          </p>
        </div>
        <button onClick={async () => { const { data: h } = await api.get("/admin/themes/history"); setShowHistory(h); }}
          data-testid="themes-history-btn"
          className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-semibold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400">
          <History className="h-4 w-4" /> History
        </button>
      </div>

      {/* Active theme strip */}
      <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-white/10 bg-[#F1EDE3] p-5" data-testid="themes-active-strip">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${live.theme ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-500"}`}>
          <Palette className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">Live right now</p>
          <p className="truncate text-sm font-bold text-white" data-testid="themes-live-name">
            {live.theme ? live.theme.name : "PAQVERO Default"}
            <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
              {live.source === "manual" ? "manual override" : live.source === "schedule" ? "via schedule" : "built-in fallback"}
            </span>
          </p>
        </div>
        {live.source === "manual" && (
          <button onClick={() => act(() => api.post("/admin/themes/deactivate"), "Manual override released")}
            data-testid="themes-release-manual"
            className="rounded-xl border border-white/10 px-4 py-2 text-xs font-bold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400">
            Release override
          </button>
        )}
        {live.scheduled?.length > 0 && (
          <div className="flex items-center gap-2 text-xs text-neutral-500" data-testid="themes-upcoming">
            <CalendarClock className="h-4 w-4 text-violet-300" />
            Next: {live.scheduled.find((s) => s.status === "scheduled")?.name || live.scheduled[0].name}
          </div>
        )}
      </div>

      {/* Theme cards */}
      {themes.length === 0 && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-dashed border-amber-500/30 bg-amber-500/5 p-5" data-testid="themes-empty-state">
          <div>
            <p className="text-sm font-bold text-white">No festival theme yet</p>
            <p className="mt-0.5 max-w-lg text-xs leading-relaxed text-neutral-400">
              Create the ready-made Diwali theme — festive Brunson headlines, STOCK UP FEST promo carousel and deal rows, lanterns and the rocket countdown bar are all included.
            </p>
          </div>
          <button
            onClick={() => act(() => api.post("/admin/themes", { preset: "diwali" }), "Diwali theme created — publish it, then activate or schedule")}
            data-testid="themes-create-diwali"
            className="rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-extrabold text-black transition-colors hover:bg-amber-400">
            Create Diwali Theme
          </button>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="themes-grid">
        {themes.map((t) => (
          <div key={t.id} className="rounded-2xl border border-white/10 bg-[#F1EDE3] p-5" data-testid={`theme-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 overflow-hidden rounded-xl border border-white/10"
                  style={{ background: t.tokens_dark?.bg || "#111" }}>
                  <span className="m-auto h-5 w-5 rounded-full" style={{ background: t.tokens_dark?.accent || "#FF9F1C" }} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-white">{t.name}</p>
                  <p className="text-[11px] capitalize text-neutral-500">{t.type}</p>
                </div>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[t.display_status] || STATUS_STYLE.draft}`}
                data-testid={`theme-status-${t.id}`}>
                {t.is_live ? "live" : t.display_status}
              </span>
            </div>
            {t.schedule?.enabled && (
              <p className="mt-3 flex items-center gap-1.5 text-[11px] text-neutral-500">
                <Clock className="h-3 w-3" />
                {fmtDT(t.schedule.start_at)} → {fmtDT(t.schedule.end_at)}
                {t.schedule.recurrence === "yearly" && " · yearly"}
              </p>
            )}
            <p className="mt-2 text-[11px] text-neutral-600">
              Updated {fmtDT(t.meta?.updated_at)} by {t.meta?.updated_by || "—"}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => setEditor(t)} data-testid={`theme-edit-${t.id}`}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400">
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button onClick={() => act(async () => {
                  const { data: link } = await api.post(`/admin/themes/${t.id}/preview-link`);
                  window.open(link.url, "_blank");
                })} data-testid={`theme-preview-${t.id}`}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-semibold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400">
                <Eye className="h-3 w-3" /> Preview
              </button>
              {t.status === "draft" && (
                <button onClick={() => act(() => api.post(`/admin/themes/${t.id}/publish`), "Published")}
                  data-testid={`theme-publish-${t.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-sky-500/30 px-3 py-1.5 text-xs font-semibold text-sky-400 hover:bg-sky-500/10">
                  <CircleCheck className="h-3 w-3" /> Publish
                </button>
              )}
              {t.status === "published" && !t.is_live && (
                <button onClick={() => act(() => api.post(`/admin/themes/${t.id}/activate`, {}), "Theme is now live")}
                  data-testid={`theme-activate-${t.id}`}
                  className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400">
                  <Play className="h-3 w-3" /> Activate
                </button>
              )}
              {t.is_live && (
                <button onClick={() => act(() => api.post("/admin/themes/deactivate"), "Theme deactivated — default restored")}
                  data-testid={`theme-deactivate-${t.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-emerald-500/30 px-3 py-1.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-500/10">
                  Deactivate
                </button>
              )}
              {t.status !== "archived" && (
                <button onClick={() => act(() => api.post(`/admin/themes/${t.id}/archive`), "Archived")}
                  data-testid={`theme-archive-${t.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-semibold text-neutral-500 hover:border-red-500/40 hover:text-red-400">
                  <Archive className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Archived themes — recoverable, never shown on the storefront */}
      {archivedThemes.length > 0 && (
        <div className="mt-8" data-testid="themes-archived-section">
          <button
            onClick={() => setShowArchived((v) => !v)}
            data-testid="themes-archived-toggle"
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-500 transition-colors hover:text-amber-400">
            <Archive className="h-3.5 w-3.5" />
            Archived ({archivedThemes.length}) {showArchived ? "— hide" : "— show"}
          </button>
          {showArchived && (
            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {archivedThemes.map((t) => (
                <div key={t.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-[#F1EDE3]/60 p-4 opacity-70" data-testid={`theme-archived-${t.id}`}>
                  <p className="truncate text-sm font-semibold text-neutral-400">{t.name}</p>
                  <button
                    onClick={() => act(() => api.post(`/admin/themes/${t.id}/unarchive`), "Unarchived — now a draft; publish it to use again")}
                    data-testid={`theme-unarchive-${t.id}`}
                    className="shrink-0 rounded-lg border border-white/10 px-3 py-1.5 text-xs font-bold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400">
                    Unarchive
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Editor */}
      {editor && (
        <ThemeEditor theme={editor} busy={busy}
          onClose={(reload) => { setEditor(null); if (reload) load(); }}
          act={act} />
      )}

      {/* History */}
      {showHistory && (
        <div className="fixed inset-x-0 bottom-0 top-[72px] z-[90] flex justify-end bg-black/60 backdrop-blur-sm" data-testid="theme-history-drawer">
          <div className="h-full w-full max-w-md overflow-y-auto border-l border-white/10 bg-[#F1EDE3] p-6" data-lenis-prevent>
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Theme History</h3>
              <button onClick={() => setShowHistory(null)} aria-label="Close" data-testid="theme-history-close"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className={LABEL}>Activation events (scheduler + manual)</p>
            <div className="mb-6 space-y-2">
              {showHistory.events.length === 0 && <p className="text-xs text-neutral-600">No events yet.</p>}
              {showHistory.events.map((e) => (
                <div key={e.id} className="rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-xs">
                  <span className="font-bold text-white">{e.theme_name || "Default"}</span>
                  <span className="text-neutral-400"> · {e.type.replace(/_/g, " ")} · by {e.by}</span>
                  <span className="mt-0.5 block text-[10px] text-neutral-600">{fmtDT(e.at)}</span>
                </div>
              ))}
            </div>
            <p className={LABEL}>Publication & edit trail</p>
            <div className="space-y-2">
              {showHistory.publications.map((h, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-xs">
                  <span className="font-bold text-white">{h.theme}</span>
                  <span className="text-neutral-400"> · {h.action}{h.note ? ` — ${h.note}` : ""} · by {h.by}</span>
                  <span className="mt-0.5 block text-[10px] text-neutral-600">{fmtDT(h.at)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Theme Editor (content, effects, schedule — design tokens are managed in code) ── */

function ThemeEditor({ theme, onClose, act, busy }) {
  const [form, setForm] = useState(() => JSON.parse(JSON.stringify(theme)));
  const [tab, setTab] = useState("content");
  const set = (path, value) => {
    setForm((f) => {
      const next = { ...f };
      const parts = path.split(".");
      let obj = next;
      for (const p of parts.slice(0, -1)) obj[p] = { ...obj[p] }, obj = obj[p];
      obj[parts.at(-1)] = value;
      return next;
    });
  };

  const setCard = (i, field, value) => {
    const cards = [...(form.content?.custom_cards || [])];
    while (cards.length <= i) cards.push({ headline: "", subline: "", image: "", link: "" });
    cards[i] = { ...cards[i], [field]: value };
    while (cards.length) {
      const last = cards[cards.length - 1];
      if (last.headline || last.subline || last.image || last.link) break;
      cards.pop();
    }
    set("content.custom_cards", cards);
  };

  const save = async () => {
    const payload = {
      name: form.name, description: form.description, type: form.type,
      tokens_dark: form.tokens_dark, tokens_light: form.tokens_light,
      content: form.content, sparkles: form.sparkles, decor: form.decor, ambient_glow: form.ambient_glow,
    };
    return act(() => api.put(`/admin/themes/${form.id}`, payload), "Saved");
  };

  const saveSchedule = async () => {
    const s = form.schedule;
    return act(async () => {
      const { data } = await api.post(`/admin/themes/${form.id}/schedule`, {
        enabled: Boolean(s.enabled),
        start_at: s.start_at || null,
        end_at: s.end_at || null,
        timezone_name: s.timezone_name || "Asia/Kolkata",
        recurrence: s.recurrence || "none",
      });
      if (data.overlap_warning) toast.warning(data.overlap_warning, { duration: 6000 });
    }, "Schedule saved");
  };

  const tabs = [
    { id: "content", label: "Content & Assets", icon: ImagePlus },
    { id: "effects", label: "Effects", icon: Sparkles },
    { id: "schedule", label: "Schedule", icon: CalendarClock },
  ];

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4 pt-[80px] backdrop-blur-sm" data-testid="theme-editor-modal">
      <div className="flex max-h-[calc(92vh-80px)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#F1EDE3]">
        <div className="flex items-start justify-between border-b border-white/10 p-5">
          <div className="min-w-0 flex-1">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="theme-editor-name"
              className="w-full bg-transparent font-display text-lg font-bold text-white focus:outline-none" />
            <p className="mt-0.5 text-xs text-neutral-500">
              {form.status === "draft" ? "Draft — edits never touch the live site until published." : `Status: ${form.status}`}
            </p>
          </div>
          <button onClick={() => onClose(true)} aria-label="Close" data-testid="theme-editor-close"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex gap-1 border-b border-white/10 px-5 pt-3">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`theme-editor-tab-${t.id}`}
              className={`flex items-center gap-1.5 rounded-t-lg px-3.5 py-2.5 text-xs font-bold transition-colors ${
                tab === t.id ? "bg-amber-500/10 text-amber-400" : "text-neutral-500 hover:text-white"}`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5" data-lenis-prevent>
          {tab === "content" && (
            <>
              <div>
                <label className={LABEL}>Description (admin only)</label>
                <input value={form.description || ""} onChange={(e) => set("description", e.target.value)}
                  className={INPUT} data-testid="theme-editor-description" />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                {[1, 2, 3].map((n) => (
                  <div key={n}>
                    <label className={LABEL}>Hero headline line {n}</label>
                    <input value={form.content?.[`hero_line${n}`] || ""} onChange={(e) => set(`content.hero_line${n}`, e.target.value)}
                      className={INPUT} data-testid={`theme-editor-hero-line${n}`} placeholder={["Stock Up.", "Stay", "Ready."][n - 1]} />
                  </div>
                ))}
              </div>
              <div>
                <label className={LABEL}>Hero subcopy</label>
                <textarea value={form.content?.hero_subcopy || ""} onChange={(e) => set("content.hero_subcopy", e.target.value)}
                  rows={2} className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white focus:border-amber-500/60 focus:outline-none"
                  data-testid="theme-editor-hero-subcopy" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>Hero CTA label</label>
                  <input value={form.content?.hero_cta_label || ""} onChange={(e) => set("content.hero_cta_label", e.target.value)}
                    className={INPUT} data-testid="theme-editor-hero-cta" placeholder="Shop PAQVERO" />
                </div>
                <div>
                  <label className={LABEL}>Promo ribbon text (truthful — no unconfigured discounts)</label>
                  <input value={form.content?.promo_text || ""} onChange={(e) => set("content.promo_text", e.target.value)}
                    className={INPUT} data-testid="theme-editor-promo" />
                </div>
                <div>
                  <label className={LABEL}>Fest ends at (IST) — rocket bar deadline; the theme switches itself off after this</label>
                  <input type="datetime-local"
                    value={(form.content?.fest_ends_at || "").replace(/([+-]\d{2}:\d{2}|Z)$/, "").slice(0, 16)}
                    onChange={(e) => set("content.fest_ends_at", e.target.value ? `${e.target.value}:00+05:30` : "")}
                    className={INPUT} data-testid="theme-editor-fest-ends" />
                </div>
              </div>
              <AssetField label="Hero banner image (replaces the product showcase)" value={form.content?.hero_banner_image}
                onChange={(v) => set("content.hero_banner_image", v)} testid="theme-editor-banner" />
              <div className="rounded-xl border border-white/10 p-4" data-testid="theme-editor-custom-cards">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-400">Custom hero cards</p>
                <p className="mt-1 text-[11px] leading-relaxed text-neutral-500">
                  Shown first in the STOCK UP FEST hero carousel. Automatic deal, category and brand cards fill in behind them.
                </p>
                {[0, 1, 2].map((i) => {
                  const card = (form.content?.custom_cards || [])[i] || {};
                  return (
                    <div key={i} className="mt-3 grid gap-3 rounded-lg border border-white/5 p-3 sm:grid-cols-2">
                      <input value={card.headline || ""} onChange={(e) => setCard(i, "headline", e.target.value)}
                        className={INPUT} placeholder={`Card ${i + 1} headline`} data-testid={`theme-editor-card-${i}-headline`} />
                      <input value={card.link || ""} onChange={(e) => setCard(i, "link", e.target.value)}
                        className={INPUT} placeholder="/shop?category=… or /shop/product/…" data-testid={`theme-editor-card-${i}-link`} />
                      <input value={card.subline || ""} onChange={(e) => setCard(i, "subline", e.target.value)}
                        className={`${INPUT} sm:col-span-2`} placeholder="Subline (optional)" data-testid={`theme-editor-card-${i}-subline`} />
                      <div className="sm:col-span-2">
                        <AssetField label={`Card ${i + 1} image (optional)`} value={card.image || ""}
                          onChange={(v) => setCard(i, "image", v)} testid={`theme-editor-card-${i}-image`} />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>Hero eyebrow (small label above the headline)</label>
                  <input value={form.content?.hero_eyebrow || ""} onChange={(e) => set("content.hero_eyebrow", e.target.value)}
                    className={INPUT} data-testid="theme-editor-hero-eyebrow" placeholder="Festive Season · Business Supplies" />
                </div>
                <div>
                  <label className={LABEL}>Headline font</label>
                  <select value={form.content?.display_font || "default"}
                    onChange={(e) => set("content.display_font", e.target.value)} className={INPUT}
                    data-testid="theme-editor-font">
                    <option value="default">PAQVERO default</option>
                    <option value="festive">Festive serif (Playfair Display)</option>
                    <option value="brunson">Brunson (vintage display)</option>
                  </select>
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <AssetField label="Logo variant — dark mode" value={form.content?.logo_dark}
                  onChange={(v) => set("content.logo_dark", v)} testid="theme-editor-logo-dark" />
                <AssetField label="Logo variant — light mode" value={form.content?.logo_light}
                  onChange={(v) => set("content.logo_light", v)} testid="theme-editor-logo-light" />
              </div>
            </>
          )}

          {tab === "effects" && (
            <>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={form.sparkles?.enabled !== false}
                  onChange={(e) => set("sparkles.enabled", e.target.checked)} data-testid="theme-editor-sparkles-enabled"
                  className="h-4 w-4 accent-amber-500" />
                Ambient sparkles (dark mode only)
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>Sparkle count (0–24)</label>
                  <input type="number" min="0" max="24" value={form.sparkles?.count ?? 14}
                    onChange={(e) => set("sparkles.count", Math.min(24, Math.max(0, Number(e.target.value) || 0)))}
                    className={INPUT} data-testid="theme-editor-sparkle-count" />
                </div>
                <ColorField label="Sparkle color" value={form.sparkles?.color}
                  onChange={(v) => set("sparkles.color", v)} testid="theme-editor-sparkle-color" />
              </div>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={form.ambient_glow !== false}
                  onChange={(e) => set("ambient_glow", e.target.checked)} data-testid="theme-editor-glow"
                  className="h-4 w-4 accent-amber-500" />
                Warm ambient glow in the hero
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.diya_hero)}
                  onChange={(e) => set("decor.diya_hero", e.target.checked)} data-testid="theme-editor-diya"
                  className="h-4 w-4 accent-amber-500" />
                Glowing diya (oil lamp) in the hero
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.rangoli_dividers)}
                  onChange={(e) => set("decor.rangoli_dividers", e.target.checked)} data-testid="theme-editor-rangoli"
                  className="h-4 w-4 accent-amber-500" />
                Rangoli section dividers
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.products_in_flame)}
                  onChange={(e) => set("decor.products_in_flame", e.target.checked)} data-testid="theme-editor-products-flame"
                  className="h-4 w-4 accent-amber-500" />
                Products rotating inside the diya flame
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.hanging_lanterns)}
                  onChange={(e) => set("decor.hanging_lanterns", e.target.checked)} data-testid="theme-editor-hanging-lanterns"
                  className="h-4 w-4 accent-amber-500" />
                Hanging lanterns at the top corners
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.sky_lanterns)}
                  onChange={(e) => set("decor.sky_lanterns", e.target.checked)} data-testid="theme-editor-sky-lanterns"
                  className="h-4 w-4 accent-amber-500" />
                Sky lanterns rising at the page edges
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.rocket_bar)}
                  onChange={(e) => set("decor.rocket_bar", e.target.checked)} data-testid="theme-editor-rocket-bar"
                  className="h-4 w-4 accent-amber-500" />
                Rocket countdown bar pinned at the top
              </label>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.decor?.fireworks)}
                  onChange={(e) => set("decor.fireworks", e.target.checked)} data-testid="theme-editor-fireworks"
                  className="h-4 w-4 accent-amber-500" />
                Sky-rocket fireworks behind the homepage hero
              </label>
              {Boolean(form.decor?.fireworks) && (
                <div className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300" data-testid="theme-editor-fireworks-color">
                  <span className="flex-1">Fireworks colour</span>
                  {[
                    ["", "Festive mix (default)"],
                    ["#d32f2f", "Diwali red"],
                    ["#2e7d32", "Royal green"],
                    ["#1565c0", "Sky blue"],
                    ["#7b1fa2", "Royal purple"],
                  ].map(([c, label]) => (
                    <button key={c || "mix"} type="button" title={label}
                      onClick={() => set("decor.fireworks_color", c)}
                      data-testid={`theme-editor-fireworks-color-${c || "mix"}`}
                      className={`h-7 w-7 rounded-full border-2 transition-transform hover:scale-110 ${((form.decor?.fireworks_color || "") === c) ? "border-amber-400" : "border-white/20"}`}
                      style={{ background: c || "linear-gradient(135deg,#ffbe3c,#ff8c1e,#ff5f2d)" }} />
                  ))}
                  <input type="color" value={form.decor?.fireworks_color || "#ffbe3c"}
                    onChange={(e) => set("decor.fireworks_color", e.target.value)}
                    data-testid="theme-editor-fireworks-color-custom"
                    className="h-8 w-10 cursor-pointer rounded border border-white/20 bg-transparent" title="Custom colour" />
                </div>
              )}
            </>
          )}

          {tab === "schedule" && (
            <>
              <label className="flex items-center gap-3 rounded-xl border border-white/10 p-4 text-sm text-neutral-300">
                <input type="checkbox" checked={Boolean(form.schedule?.enabled)}
                  onChange={(e) => set("schedule.enabled", e.target.checked)} data-testid="theme-editor-schedule-enabled"
                  className="h-4 w-4 accent-amber-500" />
                Enable automatic activation window
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={LABEL}>Starts</label>
                  <input type="datetime-local" value={form.schedule?.start_at || ""}
                    onChange={(e) => set("schedule.start_at", e.target.value)} className={INPUT}
                    data-testid="theme-editor-schedule-start" />
                </div>
                <div>
                  <label className={LABEL}>Ends</label>
                  <input type="datetime-local" value={form.schedule?.end_at || ""}
                    onChange={(e) => set("schedule.end_at", e.target.value)} className={INPUT}
                    data-testid="theme-editor-schedule-end" />
                </div>
                <div>
                  <label className={LABEL}>Timezone</label>
                  <input value={form.schedule?.timezone_name || "Asia/Kolkata"}
                    onChange={(e) => set("schedule.timezone_name", e.target.value)} className={INPUT}
                    data-testid="theme-editor-timezone" placeholder="Asia/Kolkata" />
                </div>
                <div>
                  <label className={LABEL}>Recurrence</label>
                  <select value={form.schedule?.recurrence || "none"}
                    onChange={(e) => set("schedule.recurrence", e.target.value)} className={INPUT}
                    data-testid="theme-editor-recurrence">
                    <option value="none">One time</option>
                    <option value="yearly">Repeats every year</option>
                  </select>
                </div>
              </div>
              <p className="rounded-xl border border-white/10 bg-black/40 p-4 text-xs leading-relaxed text-neutral-500">
                At the start time the server activates this theme automatically; after the end time PAQVERO Default
                returns. A manual activation always takes precedence until it is released or expires.
              </p>
              <button onClick={saveSchedule} disabled={busy} data-testid="theme-editor-schedule-save"
                className="flex items-center gap-2 rounded-xl border border-violet-500/30 px-4 py-2.5 text-sm font-bold text-violet-300 hover:bg-violet-500/10 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                Save schedule
              </button>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/10 p-5">
          <button onClick={save} disabled={busy} data-testid="theme-editor-save"
            className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2.5 text-sm font-bold text-black hover:bg-amber-400 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save changes
          </button>
          <button onClick={async () => {
              if (!(await save())) return;
              const { data: link } = await api.post(`/admin/themes/${form.id}/preview-link`);
              window.open(link.url, "_blank");
            }} disabled={busy} data-testid="theme-editor-preview"
            className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm font-semibold text-neutral-300 hover:border-amber-500/50 hover:text-amber-400 disabled:opacity-50">
            <Eye className="h-4 w-4" /> Preview on storefront
          </button>
        </div>
      </div>
    </div>
  );
}
